import {PammOfferType} from '../../../graphql/types'
import {OfferNode} from '../types'
import {AUTO_CREATED_OFFER_NAME} from './offerDisplayName'

/**
 * Extracts the agent chain of an offer as an ordered array of agent IDs.
 *
 * The backend stores the chain ordered `[ownAgent, parentAgent, grandparentAgent, ...]`,
 * so index 0 is the agent that owns the offer and each following entry walks one level
 * up the hierarchy. An empty array marks a Money Manager's own offer.
 */
export const getOfferChainIds = (offer: PammOfferType): number[] =>
  (offer.settings?.agentChain ?? [])
    .map((agent) => agent?.agentId)
    .filter((agentId): agentId is number => typeof agentId === 'number')

/**
 * Serialises a manager-scoped agent chain into a stable map key.
 *
 * The key is scoped by `managerId` because an agent uses the same agent id across every
 * manager they work under. Without the manager scope, offers belonging to different
 * managers but sharing an agent chain collide, and children attach to the wrong manager's
 * parent — orphaning the other manager's subtree (PT-5423).
 */
const toChainKey = (managerId: number | null | undefined, chainIds: number[]): string =>
  `${managerId ?? ''}:${chainIds.join(',')}`

/** Deterministic ordering by offer id so the tree renders identically on every load. */
const byOfferId = (a: OfferNode, b: OfferNode): number => (a.offer.id ?? 0) - (b.offer.id ?? 0)

/**
 * Walks the tree assigning each node its depth relative to the rendered root and back-linking
 * every child to its parent (roots get `undefined`), so a node can later read its parent's
 * figures — e.g. an investor node's share of the parent's AUM (PT-5712).
 */
const assignLevels = (node: OfferNode, level: number, parent: OfferNode | undefined): void => {
  node.level = level
  node.parent = parent
  node.children.sort(byOfferId)
  node.children.forEach((child) => assignLevels(child, level + 1, node))
}

/**
 * Reconstructs the hierarchical PAMM offers tree from a flat list of offers.
 *
 * An offer's parent is the offer whose agent chain equals this offer's chain with the
 * first (own) agent removed — e.g. a sub-agent offer `[B, A]` hangs under agent offer `[A]`,
 * which in turn hangs under the Money Manager offer `[]`. Offers whose parent is not part
 * of the supplied data set (the agent themselves in the Agent view, or any gap in the data)
 * are promoted to roots. The result is safe to render recursively: chain links strictly shorten
 * towards the root, and a joined-offer link that would close a cycle is skipped.
 *
 * A chain-less offer is a Money Manager's own offer and is always a root: a manager may hold
 * several independent roots, and none of them is nested under another (PT-6881). The chain of a
 * first-level agent (chain length 1) does not say which of these roots the agent belongs to. The
 * tree picks the parent of such an agent offer in this order:
 *  1. the offer the agent joined the manager through as an investor (`agentJoinedOfferId`,
 *     PT-6731). Product reads an agent as part of the offer the agent came from, not of the
 *     auto-created "Default" offer. The offer is a manager's own offer or the offer of another
 *     first-level agent of the same manager: an agent who joins through agent 1 hangs under agent 1.
 *     A deeper agent offer, an offer of another manager or an offer outside the set never adopts.
 *     A link that would close a cycle is skipped.
 *  2. the stored Main Offer (PT-6881). It is a preference, not a link. It also decides which root
 *     reads first.
 *  3. the lowest-id root (PT-5875).
 * A chain of length 2 or more always follows the chain.
 *
 * The auto-created "Default" offer is an empty placeholder. The tree drops it when no agent hangs
 * under it and it holds no investors and no AUM, unless it is the Main Offer. A "Default" offer
 * that carries agents or money stays, as before (PT-5875).
 *
 * @param offers - Flat list of offers in the viewer's scope (order does not matter).
 * @param mainOfferId - The Money Manager's stored Main Offer, if any.
 * @returns Root nodes, each carrying its depth-based `level` and nested `children`.
 */
export const buildOfferTree = (
  offers: PammOfferType[],
  mainOfferId?: number | null,
): OfferNode[] => {
  const seenIds = new Set<number>()
  const nodes: OfferNode[] = []
  offers.forEach((offer) => {
    if (typeof offer.id === 'number') {
      if (seenIds.has(offer.id)) return
      seenIds.add(offer.id)
    }
    nodes.push({offer, level: 0, children: []})
  })

  // Index every node by its own chain key so a child can look its parent up in O(1).
  const nodesByChainKey = new Map<string, OfferNode[]>()
  nodes.forEach((node) => {
    const key = toChainKey(node.offer.managerId, getOfferChainIds(node.offer))
    const bucket = nodesByChainKey.get(key)
    if (bucket) bucket.push(node)
    else nodesByChainKey.set(key, [node])
  })
  nodesByChainKey.forEach((bucket) => bucket.sort(byOfferId))

  const isMain = (node: OfferNode): boolean =>
    typeof mainOfferId === 'number' && node.offer.id === mainOfferId

  const isAutoCreated = (node: OfferNode): boolean => node.offer.name === AUTO_CREATED_OFFER_NAME

  const roots: OfferNode[] = []
  const parentOf = new Map<OfferNode, OfferNode>()
  const attach = (child: OfferNode, parent: OfferNode): void => {
    parent.children.push(child)
    parentOf.set(child, parent)
  }
  const firstLevelAgents: OfferNode[] = []

  nodes.forEach((node) => {
    const chainIds = getOfferChainIds(node.offer)
    // The manager's own offers are independent roots (PT-6881).
    if (chainIds.length === 0) {
      roots.push(node)
      return
    }
    // A first-level agent looks for its parent below, once every other link is in place.
    if (chainIds.length === 1) {
      firstLevelAgents.push(node)
      return
    }
    // A chain of length 2 or more always follows the chain. An agent who owns several offers at one
    // chain position gets the lowest-id parent.
    const parentKey = toChainKey(node.offer.managerId, chainIds.slice(1))
    const parent = (nodesByChainKey.get(parentKey) ?? []).find((candidate) => candidate !== node)
    if (parent) attach(node, parent)
    else roots.push(node)
  })

  const nodesById = new Map<number, OfferNode>()
  nodes.forEach((node) => {
    if (typeof node.offer.id === 'number') nodesById.set(node.offer.id, node)
  })

  /** Whether `ancestor` is `node` or sits above it in the links made so far. */
  const isAboveOrSame = (ancestor: OfferNode, node: OfferNode): boolean => {
    for (let current: OfferNode | undefined = node; current; current = parentOf.get(current)) {
      if (current === ancestor) return true
    }
    return false
  }

  // Agents of chain length 1 find every root of the manager here. Their chain does not say which
  // root they belong to. The offer the agent joined through adopts them (PT-6731): a manager's own
  // offer, or the offer of another first-level agent of the same manager when the agent came in
  // through that agent. A deeper agent offer, an offer of another manager and an offer outside the
  // set never adopt. A link that would close a cycle is skipped. Else the Main Offer adopts the
  // agent, else the lowest-id root does (PT-5875), so an agent with no downline never owns the
  // whole agent level.
  // The lowest-id agent offer links first, so the result does not depend on the input order.
  firstLevelAgents.sort(byOfferId).forEach((node) => {
    const managerOffers = nodesByChainKey.get(toChainKey(node.offer.managerId, [])) ?? []
    const candidates = managerOffers.filter((candidate) => candidate !== node)
    const joinedId = node.offer.agentJoinedOfferId
    const joined = typeof joinedId === 'number' ? nodesById.get(joinedId) : undefined
    const joinedIsUsable =
      joined !== undefined &&
      joined.offer.managerId === node.offer.managerId &&
      getOfferChainIds(joined.offer).length <= 1 &&
      // Another offer of the same agent is a sibling, not a parent.
      getOfferChainIds(joined.offer)[0] !== getOfferChainIds(node.offer)[0] &&
      !isAboveOrSame(node, joined)
    const parent = (joinedIsUsable && joined) || candidates.find(isMain) || candidates[0]
    if (parent) attach(node, parent)
    else roots.push(node)
  })

  // Drop an empty auto-created "Default" offer: it is a placeholder nobody uses (PT-6731).
  const visibleRoots = roots.filter(
    (root) =>
      !(
        isAutoCreated(root) &&
        root.children.length === 0 &&
        !isMain(root) &&
        !root.offer.activeInvestmentCount &&
        !root.offer.aum
      ),
  )

  // The Main Offer reads first, then the order is stable by offer id.
  visibleRoots.sort((a, b) => Number(isMain(b)) - Number(isMain(a)) || byOfferId(a, b))
  visibleRoots.forEach((root) => assignLevels(root, 0, undefined))
  return visibleRoots
}
